// AdviserDetailsPage.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import Sidebar from "../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import { subscribeAdviserFullDetailsByEmailOrId } from "../../../context/UserLiveDetails";
import "../../../css/Adviser/AdviserDetailsPage.css";
import {
  safe,
  asList,
  formatAddress,
  formatUKPhone,
  initialsFrom,
  cssColor,
  fmtMaybeDate,
  fmtMaybeMonthYear,
  statusColor,
  QUAL_GROUPS,
  pickTruthy,
} from "./utils/formatters";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { firestore as db, storage } from "../../../backend/firebaseconfig";
import { useUserPermissions } from "../../../context/UserPermissionsContext";
import { ReactComponent as BackIcon } from "../../../assets/icons/Back.svg";
import { useNavigate } from "react-router-dom";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import AdviserDetails from "./components/AdviserDetails/AdviserDetails";
import { Chip, PostCard, Row } from "./helperFunctions/HelperFunctions";
import ProfessionalQualifications from "./components/ProfessionalQualifications/ProfessionalQualifications";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import "../../../css/Adviser/MyProfilePage.css";
import { useUserEmail } from "../../../context/UserEmailContext";
// import FinancialPromotionsTab from "../Tabs/FinancialPromotions/FinancialPromotionsTab";
// import AdviserAMLTab from "../Tabs/AML/AdviserAMLTab";
/* ---------------- page ---------------- */

//  TESTING ONLY — set an email here to load a specific adviser profile.
// Remove or set to "" before committing.
// const TEST_ADVISER_KEY = "paul.strongitharm@rosemountpartners.co.uk";

export default function MyProfilePage() {
  const { state } = useLocation();
  const params = useParams();
    // const { userPermissions } = useUserPermissions();
    const {userEmail} = useUserEmail();

    const rawKey = 
        // TEST_ADVISER_KEY
        // ||
      userEmail ||
    params.adviserKey ||
    params.id ||
    params.email ||
    params.adviserId ||
    "";
  const adviserKey = rawKey ? decodeURIComponent(rawKey) : "";
  const [activeTab, setActiveTab] = useState("adviserDetails");

  const [details, setDetails] = useState(null);
  const proQuals = details?.professionalQualifications;
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState("");
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [websiteValue, setWebsiteValue] = useState("");
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const { userPermissions } = useUserPermissions();
  const canUploadProfile =
    Array.isArray(userPermissions) &&
    (userPermissions.includes("Adviser Support") ||
      userPermissions.includes("Administrator"));
  const navigate = useNavigate();

  // const canUploadProfile = permissions?.includes("Adviser Support");

  // console.log("User permissions:", userPermissions);

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingProfile(true);
      setProfileUploadError("");

      // optional validation
      if (!file.type.startsWith("image/")) {
        throw new Error("Please upload an image file.");
      }

      // adjust this depending on how your adviser doc id is stored
      const adviserEmail =
        adviserKey || details?.adviserDoc?.email || details?.email || "";

      if (!adviserEmail) {
        throw new Error("Adviser email not found.");
      }

      const fileExt = file.name.split(".").pop();
      const filePath = `advisers/${adviserEmail}/profile/profile.${fileExt}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // adjust collection name/path if needed
      const adviserRef = doc(
        db,
        "Authorized Members",
        "Advisers",
        "Data",
        adviserEmail,
      );
      // /Authorized Members/Advisers/Data/

      await updateDoc(adviserRef, {
        profileUrl: downloadURL,
      });

      // optional local UI update so it changes immediately
      setDetails((prev) => ({
        ...prev,
        adviserDoc: {
          ...(prev?.adviserDoc || {}),
          profileUrl: downloadURL,
        },
      }));
    } catch (err) {
    //   console.error("Profile upload failed:", err);
      setProfileUploadError(err.message || "Failed to upload profile image.");
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleSaveWebsite = useCallback(async () => {
    try {
      setSavingWebsite(true);
      setWebsiteError("");

      const adviserEmail =
        adviserKey || details?.adviserDoc?.email || details?.email || "";

      if (!adviserEmail) {
        throw new Error("Adviser email not found.");
      }

      let website = (websiteValue || "").trim();

      if (website && !/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }

      const adviserRef = doc(
        db,
        "Authorized Members",
        "Advisers",
        "Data",
        adviserEmail,
      );

      await updateDoc(adviserRef, {
        website,
      });

      setDetails((prev) => ({
        ...prev,
        adviserDoc: {
          ...(prev?.adviserDoc || {}),
          website,
        },
      }));

      setWebsiteValue(website);
      setEditingWebsite(false);
    } catch (err) {
    //   console.error("Website save failed:", err);
      setWebsiteError(err.message || "Failed to save website.");
    } finally {
      setSavingWebsite(false);
    }
  }, [adviserKey, details, websiteValue]);

  useEffect(() => {
    if (!adviserKey) return;
    return subscribeAdviserFullDetailsByEmailOrId(adviserKey, setDetails);
  }, [adviserKey]);

  useEffect(() => {
    setWebsiteValue(details?.adviserDoc?.website || state?.website || "");
  }, [details, state]);

  const loading = !details || details.loading;

  const canViewAdminTabs = useMemo(() => {
    const permissions = Array.isArray(userPermissions) ? userPermissions : [];

    return permissions.some((permission) => {
      const value = String(permission || "")
        .trim()
        .toLowerCase();

      return (
        // value.includes("admin") ||
        value.includes("administrator")
        // ||
        // value.includes("finance") ||
        // value.includes("compliance")
      );
    });
  }, [userPermissions]);

  /**
   * Build header using:
   * 1) LIVE details (personalDetails + top-level)
   * 2) fallback to router state
   */
  const header = useMemo(() => {
    const p = details?.personalDetails || {};
    const base = details?.adviserDoc || {}; // base doc (live)
    const a = state || {}; // fallback if still present

    const nameFromLive = [p?.forename, p?.surname]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      name:
        nameFromLive ||
        a.name ||
        [a.firstName, a.lastName].filter(Boolean).join(" ") ||
        "Adviser",

      email: base?.email || details?.email || a.email || "-",
      website: base?.website || a.website || "",
      // live first, fallback to state
      status: asList(base?.status ?? a.status),
      isAR: !!(base?.isAR ?? a.isAR),
      primaryColor:
        base?.PrimaryColor || base?.primaryColor || a.primaryColor || "",
      tradingStyle:
        base?.tradingStyle ||
        base?.tradingName ||
        asList(base?.tradingStyles)[0] ||
        a.tradingStyle ||
        "",
      tradingName: base?.tradingName || a.tradingName || "",
      tradingStyles: asList(base?.tradingStyles ?? a.tradingStyles),
      fcaNumber: base?.fcaNumber || a.fcaNumber || "",

      // business address from Personal Details
      businessAddress:
        p?.businessAddress?.fullAddress || a.businessAddress || "",

      // logo from live listener
      logoUrl: details?.logoUrl || a.logoUrl || "",
      profileUrl: base?.profileUrl || details?.profileUrl || a.profileUrl || "",
      website: base?.website || a.website || "",
    };
  }, [details, state]);

//   console.log("Header data:", header);

  const statusBg = useMemo(
    () => statusColor(asList(header.status)),
    [header.status],
  );

  const avatarText = useMemo(() => initialsFrom(header.name), [header.name]);

  const breadcrumbItems = useMemo(
    () => [
      { label: "Dashboard", to: "/my-dashboard" },
      { label: "My Profile" },
    ],
    [],
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: "adviserDetails", label: "Details" },
      {
        id: "professionalQualifications",
        label: "Professional Qualifications",
      },
    ];

    // if (canViewAdminTabs) {
    //   baseTabs.push(
    //     {
    //       id: "check2",
    //       label: "check²",
    //     },
    //     {
    //       id: "financialPromotions",
    //       label: "Financial Promotions",
    //     },
    //   );
    // }

    return baseTabs;
  }, [canViewAdminTabs]);

  return (
    <div className="Main-Page">
      <Helmet>
        <title>{header.name} • Adviser</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container Lavender AdviserDetailsPage">
        <Breadcrumb items={breadcrumbItems} />
        {/* <BackIcon
          className="backIcon"
          onClick={() => {
              navigate(-1);
            }}
        /> */}
        <div className="smProfilePage">
          {/* Cover */}
          <div
            className="smCover profileBanner"
            style={{ background: cssColor(header.primaryColor) }}
          >
            <div>
              <h3 className="smCoverTitle">
                {header.tradingStyle || header.name}
              </h3>
              {header.fcaNumber ? (
                <div className="smCoverSub Content-Small">
                  FRN {header.fcaNumber}
                </div>
              ) : null}
            </div>
            {header.logoUrl && (
              <div className="profileHeaderRight">
                <div className="profileLogoRight">
                  <img
                    className="smLogoImg-directory"
                    src={header.logoUrl}
                    alt="Logo"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "";
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                {/* <img
                src={adviser.logoUrl}
                alt="Logo"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null; // stop loops
                  e.currentTarget.src = ""; // remove request
                  e.currentTarget.style.display = "none";
                }}
              /> */}
              </div>
            )}
          </div>

          {/* Profile header */}
          <div className="smHeader">
            <div className="smHeaderLeft">
              <div className="smIdentity">
                <div className="smNameRow">
                  <h2 className="smName Content-SemiBold">{header.name}</h2>
                  {header.isAR ? <span className="smBadge">AR</span> : null}
                </div>

                <div className="smBio Content-Small">
                  <a href={`mailto:${header.email}`}>{header.email}</a>
                </div>
                <div className="smBio Content-Small">
                  <a
                    href={`${header.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {header.website}
                  </a>
                </div>
                <div className="smBio Content-Small">
                  {header.tradingStyle ? header.tradingStyle : "-"}
                  {header.fcaNumber ? (
                    <>
                      <span className="dot"> •</span> FRN {header.fcaNumber}
                    </>
                  ) : null}
                </div>

                <div className="smMetaGroups">
                  {/* STATUS */}
                  <div className="smMetaGroup">
                    <div className="smMetaLabel Content-Small muted">
                      Status
                    </div>
                    {asList(header.status).length > 0 ? (
                      <div className="smChips">
                        {asList(header.status)
                          .slice(0, 6)
                          .map((s) => (
                            <Chip
                              key={`st-${s}`}
                              style={{ background: statusBg, color: "#fff" }}
                            >
                              {s}
                            </Chip>
                          ))}
                      </div>
                    ) : (
                      <div className="Content-Small muted">-</div>
                    )}
                  </div>

                  {/* TRADING STYLES */}
                  <div className="smMetaGroup">
                    <div className="smMetaLabel Content-Small muted">
                      Trading styles
                    </div>

                    {asList(header.tradingStyles).length > 0 ? (
                      <div className="smChips">
                        {asList(header.tradingStyles)
                          .slice(0, 6)
                          .map((s) => (
                            <Chip key={`ts-${s}`} className="smChipTrading">
                              {s}
                            </Chip>
                          ))}
                      </div>
                    ) : (
                      <div className="Content-Small muted">-</div>
                    )}
                  </div>
                </div>

                {/* Actions */}

                <div className="smActionsLeft">
                  {canUploadProfile && (
                    <>
                      <PrimaryButton
                        label={
                          uploadingProfile
                            ? "Uploading..."
                            : "Upload profile image"
                        }
                        onClick={() => {
                          const input =
                            document.getElementById("profileImageInput");
                          if (input) input.click();
                        }}
                        disabled={uploadingProfile}
                      />
                      <input
                        id="profileImageInput"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleProfileImageUpload}
                        disabled={uploadingProfile}
                      />
                    </>

                    // <label
                    //   className="smBtnPrimary Content-Small"
                    //   style={{ cursor: "pointer" }}
                    // >
                    //   {uploadingProfile
                    //     ? "Uploading..."
                    //     : "Upload profile image"}
                    //   <input
                    //     type="file"
                    //     accept="image/*"
                    //     onChange={handleProfileImageUpload}
                    //     style={{ display: "none" }}
                    //     disabled={uploadingProfile}
                    //   />
                    // </label>
                  )}

                  {profileUploadError ? (
                    <div
                      className="Content-Small"
                      style={{ color: "red", marginTop: 6 }}
                    >
                      {profileUploadError}
                    </div>
                  ) : null}
                  {websiteError ? (
                    <div
                      className="Content-Small"
                      style={{ color: "red", marginTop: 6 }}
                    >
                      {websiteError}
                    </div>
                  ) : null}

                  {/* <div className="smChips">
                    {editingWebsite ? (
                      <div className="smChips">
                        <input
                          type="text"
                          className="Content-Small"
                          value={websiteValue}
                          onChange={(e) => setWebsiteValue(e.target.value)}
                          placeholder="Enter website URL"
                          style={{
                            minWidth: 220,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #ccc",
                          }}
                        />
                        <button
                          className="smBtnPrimary Content-Small"
                          onClick={handleSaveWebsite}
                          disabled={savingWebsite}
                        >
                          {savingWebsite ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="smBtnGhost Content-Small"
                          onClick={() => {
                            setEditingWebsite(false);
                            setWebsiteValue(header.website || "");
                            setWebsiteError("");
                          }}
                          disabled={savingWebsite}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : header.website ? (
                      <>
                        <a
                          className="smBtnGhost Content-Small"
                          href={header.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Website
                        </a>
                        <button
                          className="smBtnPrimary Content-Small"
                          onClick={() => setEditingWebsite(true)}
                        >
                          Edit website
                        </button>
                      </>
                    ) : (
                      <PrimaryButton
                        label="Add website"
                        onClick={() => setEditingWebsite(true)}
                      />
                      // <button
                      //   className="smBtnPrimary Content-Small"
                      //   onClick={() => setEditingWebsite(true)}
                      // >
                      //   Add website
                      // </button>
                    )}
                  </div> */}
                  {userPermissions.includes("Adviser Support") ||
                  userPermissions.includes("Compliance") ? (
                    <SecondaryButton
                      label="Edit Details"
                      onClick={() =>
                        navigate(
                          `/my-profile/${encodeURIComponent(adviserKey || header.email)}/edit`,
                          {
                            state: {
                              header,
                              details,
                            },
                          },
                        )
                      }
                    />
                  ) : null}
                </div>

                {/* {header.email && header.email !== "-" ? (
                    <a
                      className="smBtnPrimary Content-Small"
                      href={`mailto:${header.email}`}
                    >
                      Message
                    </a>
                  ) : (
                    <button className="smBtnPrimary Content-Small" disabled>
                      Message
                    </button>
                  )} */}

                {/* {details?.personalDetails?.mobileTelephone ? (
                    <a
                      className="smBtnGhost Content-Small"
                      href={`tel:${String(
                        details.personalDetails.mobileTelephone,
                      ).replace(/\s/g, "")}`}
                    >
                      Call
                    </a>
                  ) : (
                    <button className="smBtnGhost Content-Small" disabled>
                      Call
                    </button>
                  )} */}

                {/* <button
                    className="smBtnGhost Content-Small"
                    onClick={() =>
                      navigator.clipboard?.writeText(header.email || "")
                    }
                    disabled={!header.email || header.email === "-"}
                    title="Copy email"
                  >
                    Copy email
                  </button> */}
              </div>
            </div>

            {/* RIGHT */}
            <div className="smHeaderRight">
              <div className="smLogoOnly">
                {header.profileUrl ? (
                  <img
                    className="smLogoImg"
                    src={header.profileUrl}
                    alt={header.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "";
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="smLogoFallback Content-SemiBold">
                    {avatarText}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => {
              setActiveTab(id);
              // only 1 tab for now, so no need to actually change it
            }}
          />
          <div className="smsizedBox" />

          {/* Feed */}
          {(() => {
            switch (activeTab) {
              case "adviserDetails":
                return (
                  <AdviserDetails
                    details={details}
                    loading={loading}
                    header={header}
                  />
                );

              case "professionalQualifications":
                return <ProfessionalQualifications details={details} />;

              //   case "check2":
              //     return <AdviserAMLTab adviserEmail={header.email} />;

              //   case "financialPromotions":
              //     return <FinancialPromotionsTab adviserEmail={header.email} />;

              default:
                return null;
            }
          })()}
        </div>
      </div>
    </div>
  );
}
